import axios from 'axios';
import { getAuthHeader } from './authService';

const API_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface Appointment {
  id: number;
  studentId: number;
  providerId: number;
  appointmentDate: string;
  timeSlot: string;
  message?: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  provider?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  student?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CreateAppointmentRequest {
  studentId?: number;
  providerId: number;
  appointmentDate: string;
  timeSlot: string;
  message?: string;
}

export interface UpdateAppointmentRequest {
  appointmentDate?: string;
  timeSlot?: string;
  message?: string;
  status?: string;
}

export interface RescheduleAppointmentRequest {
  appointmentDate: string;
  timeSlot: string;
  message?: string;
}

export interface PagedAppointmentsResponse {
  content: Appointment[];
  nextCursor?: string;
  hasNext: boolean;
  totalElements?: number;
}

export interface AvailabilityResponse {
  date: string;
  availableSlots: string[];
}

class AppointmentService {
  // Get student appointments
  async getMyAppointments(
    cursor?: string,
    limit: number = 20,
    status?: string,
    startDate?: string,
    endDate?: string
  ): Promise<PagedAppointmentsResponse> {
    const params = new URLSearchParams();
    if (cursor) params.append('cursor', cursor);
    params.append('limit', limit.toString());
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await axios.get(`${API_URL}/appointments?${params.toString()}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Get provider appointments
  async getProviderAppointments(
    cursor?: string,
    limit: number = 20,
    status?: string,
    startDate?: string,
    endDate?: string,
    studentId?: number
  ): Promise<PagedAppointmentsResponse> {
    const params = new URLSearchParams();
    if (cursor) params.append('cursor', cursor);
    params.append('limit', limit.toString());
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (studentId) params.append('studentId', studentId.toString());

    const response = await axios.get(`${API_URL}/appointments?${params.toString()}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Get all appointments (admin)
  async getAllAppointments(
    cursor?: string,
    limit: number = 20,
    status?: string,
    startDate?: string,
    endDate?: string,
    providerId?: number,
    studentId?: number
  ): Promise<PagedAppointmentsResponse> {
    const params = new URLSearchParams();
    if (cursor) params.append('cursor', cursor);
    params.append('limit', limit.toString());
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (providerId) params.append('providerId', providerId.toString());
    if (studentId) params.append('studentId', studentId.toString());

    const response = await axios.get(`${API_URL}/appointments?${params.toString()}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Get appointment by ID
  async getAppointmentById(id: number): Promise<Appointment> {
    const response = await axios.get(`${API_URL}/appointments/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Create appointment
  async createAppointment(request: CreateAppointmentRequest): Promise<Appointment> {
    const response = await axios.post(`${API_URL}/appointments`, request, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Update appointment
  async updateAppointment(id: number, request: UpdateAppointmentRequest): Promise<Appointment> {
    const response = await axios.put(`${API_URL}/appointments/${id}`, request, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Approve appointment (provider/admin)
  async approveAppointment(id: number): Promise<Appointment> {
    const response = await axios.put(`${API_URL}/appointments/${id}/approve`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Reject appointment (provider/admin)
  async rejectAppointment(id: number, reason?: string): Promise<Appointment> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await axios.put(`${API_URL}/appointments/${id}/reject${params}`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Cancel appointment
  async cancelAppointment(id: number, reason?: string): Promise<Appointment> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await axios.put(`${API_URL}/appointments/${id}/cancel${params}`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Complete appointment (provider)
  async completeAppointment(id: number, notes?: string): Promise<Appointment> {
    const params = notes ? `?notes=${encodeURIComponent(notes)}` : '';
    const response = await axios.put(`${API_URL}/appointments/${id}/complete${params}`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Reschedule appointment
  async rescheduleAppointment(id: number, request: RescheduleAppointmentRequest): Promise<Appointment> {
    const response = await axios.put(`${API_URL}/appointments/${id}/reschedule`, request, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Check provider availability
  async getProviderAvailability(providerId: number, date: string): Promise<AvailabilityResponse> {
    const response = await axios.get(`${API_URL}/appointments/availability/${providerId}?date=${date}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }

  // Get available providers
  async getAvailableProviders(): Promise<any[]> {
    const response = await axios.get(`${API_URL}/users/providers`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
}

export default new AppointmentService();